import React from 'react';
import { footerLinks, contactInfo, socialLinks } from '../data/mock';
import { Mail, Phone, ArrowUpRight, Instagram, Linkedin, MessageCircle, Globe, Github } from 'lucide-react';
import { Link } from 'react-router-dom';

// Custom Eventbrite Icon component - Official logo with Emerald Green styling
const EventbriteIcon = ({ className }) => (
  <img 
    src="/assets/logos/eventbrite-logo.png" 
    alt="Eventbrite" 
    className={className}
    style={{ 
      // CSS filter to convert white to emerald green #284139
      filter: 'brightness(0) saturate(100%) invert(18%) sepia(18%) saturate(800%) hue-rotate(113deg) brightness(97%) contrast(91%)',
      objectFit: 'contain'
    }}
  />
);

const Footer = ({ isDarkMode }) => {
  // Social media with proper icons and colors
  const socialData = [
    { 
      name: 'Instagram', 
      url: 'https://www.instagram.com/pathways.africa?igsh=MW15bXpja3llMXM5Zg==', 
      icon: Instagram,
      isComponent: true
    },
    { 
      name: 'LinkedIn', 
      url: 'https://www.linkedin.com/company/pathwaysafrica/', 
      icon: Linkedin,
      isComponent: true
    },
    { 
      name: 'WhatsApp', 
      url: 'https://wa.me/254795646194', 
      icon: MessageCircle,
      isComponent: true
    },
    { 
      name: 'Eventbrite', 
      url: 'https://www.eventbrite.com/e/path-to-top-usa-and-uk-universities-and-colleges-tickets-1981587263849?aff=ebdsshcopyurl&utm-campaign=social&utm-content=attendeeshare&utm-medium=discovery&utm-term=organizer-profile&utm-share-source=organizer-profile', 
      icon: EventbriteIcon,
      isComponent: false
    },
  ];

  return (
    <footer className={`border-t ${isDarkMode ? 'bg-[#111A19] border-[#284139]' : 'bg-[#284139] border-[#284139]'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Logo & Social Column */}
          <div className="lg:col-span-4">
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="/assets/logos/pathways.png" 
                alt="Pathways Africa"
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className={`text-sm leading-relaxed mb-8 max-w-sm ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/80'}`}>
              A transformative college counseling and leadership development program
              designed to support high-achieving African students in their pursuit
              of world-class higher education.
            </p>

            {/* Social Media - Modern Grid Layout */}
            <div className="space-y-4">
              <h4 className={`font-semibold text-sm tracking-wider uppercase ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
                Connect With Us
              </h4>
              <div className="flex flex-wrap gap-3">
                {socialData.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative w-12 h-12 rounded-xl bg-[#E5D9B6] border border-[#284139] flex items-center justify-center transition-all duration-300 hover:scale-110"
                    title={social.name}
                  >
                    {social.isComponent ? (
                      <social.icon className="w-5 h-5 text-[#284139]" />
                    ) : (
                      <social.icon className="w-5 h-5" />
                    )}
                    
                    {/* Tooltip */}
                    <span className={`absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg border text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                      isDarkMode ? 'bg-[#1a2625] border-[#284139] text-[#f5f5f5]' : 'bg-[#E5D9B6] border-[#284139] text-[#284139]'
                    }`}>
                      {social.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className={`font-semibold text-sm tracking-wider uppercase mb-7 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
              Quick Links
            </h4>
            <ul className="space-y-4">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className={`group text-sm transition-colors duration-200 inline-flex items-center gap-1 ${
                      isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
                    }`}
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div className="lg:col-span-2">
            <h4 className={`font-semibold text-sm tracking-wider uppercase mb-7 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
              Programs
            </h4>
            <ul className="space-y-4">
              {footerLinks.programs.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className={`group text-sm transition-colors duration-200 inline-flex items-center gap-1 ${
                      isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
                    }`}
                  >
                    {link.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="lg:col-span-2">
            <h4 className={`font-semibold text-sm tracking-wider uppercase mb-7 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
              Resources
            </h4>
            <ul className="space-y-4">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  {link.href.startsWith('/') ? (
                    <Link
                      to={link.href}
                      className={`group text-sm transition-colors duration-200 inline-flex items-center gap-1 ${
                        isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
                      }`}
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className={`group text-sm transition-colors duration-200 inline-flex items-center gap-1 ${
                        isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
                      }`}
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Section - Separate Row */}
        <div className={`mt-14 pt-14 border-t ${isDarkMode ? 'border-[#284139]' : 'border-[#E5D9B6]/20'}`}>
          <h4 className={`font-semibold text-sm tracking-wider uppercase mb-8 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
            Contact Us
          </h4>
          <div className="flex flex-col gap-6">
            {/* Email */}
            <a
              href={`mailto:${contactInfo.email}`}
              className={`group relative flex items-center gap-4 transition-colors duration-200 ${
                isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
              }`}
            >
              <div className={`relative w-12 h-12 min-w-[48px] rounded-xl flex items-center justify-center transition-all ${
                isDarkMode 
                  ? 'bg-[#1a2625] border border-[#284139] group-hover:bg-[#284139] group-hover:border-[#E5D9B6]/30'
                  : 'bg-[#E5D9B6]/20 border border-[#E5D9B6]/30 group-hover:bg-[#E5D9B6]/30'
              }`}>
                <Mail className="w-5 h-5" />
                {/* Tooltip */}
                <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none ${
                  isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                }`}>
                  Email Us
                </span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs mb-1 ${isDarkMode ? 'text-[#809076]/60' : 'text-[#E5D9B6]/60'}`}>Email</span>
                <span className="text-sm font-medium">{contactInfo.email}</span>
              </div>
            </a>

            {/* Phone Numbers */}
            {contactInfo.phones.map((phone) => (
              <a
                key={phone.label}
                href={`tel:${phone.number}`}
                className={`group relative flex items-center gap-4 transition-colors duration-200 ${
                  isDarkMode ? 'text-[#809076] hover:text-[#E5D9B6]' : 'text-[#E5D9B6]/70 hover:text-[#E5D9B6]'
                }`}
              >
                <div className={`relative w-12 h-12 min-w-[48px] rounded-xl flex items-center justify-center transition-all ${
                  isDarkMode 
                    ? 'bg-[#1a2625] border border-[#284139] group-hover:bg-[#284139] group-hover:border-[#E5D9B6]/30'
                    : 'bg-[#E5D9B6]/20 border border-[#E5D9B6]/30 group-hover:bg-[#E5D9B6]/30'
                }`}>
                  <Phone className="w-5 h-5" />
                  {/* Tooltip */}
                  <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none ${
                    isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                  }`}>
                    Call Us
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs mb-1 ${isDarkMode ? 'text-[#809076]/60' : 'text-[#E5D9B6]/60'}`}>{phone.label}</span>
                  <span className="text-sm font-medium">{phone.number}</span>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-20 pt-8 border-t ${isDarkMode ? 'border-[#284139]' : 'border-[#E5D9B6]/20'}`}>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <p className={`text-sm ${isDarkMode ? 'text-[#809076]/60' : 'text-[#E5D9B6]/60'}`}>
              © {new Date().getFullYear()} Pathways Africa. All rights reserved.
            </p>
            
            {/* Developer Credit Card */}
            <div className={`flex flex-col items-center px-6 py-4 rounded-xl ${isDarkMode ? 'bg-[#1a2625] border border-[#284139]' : 'bg-[#E5D9B6]/20 border border-[#E5D9B6]/30'}`}>
              <p className={`text-xs font-semibold tracking-wider uppercase mb-2 ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#E5D9B6]'}`}>
                Website Building Consulting
              </p>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/80'}`}>
                Site Developed by Sean Donovan (Upenn '27)
              </p>
              <div className="flex items-center gap-3">
                <a
                  href="https://seandonovan.netlify.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${isDarkMode ? 'bg-[#284139] text-[#809076] hover:text-[#f5f5f5]' : 'bg-[#284139]/20 text-[#E5D9B6] hover:bg-[#284139]/40'}`}
                >
                  <Globe className="w-4 h-4" />
                  <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                  }`}>
                    Developer's Website
                  </span>
                </a>
                <a
                  href="https://github.com/INTSEAN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${isDarkMode ? 'bg-[#284139] text-[#809076] hover:text-[#f5f5f5]' : 'bg-[#284139]/20 text-[#E5D9B6] hover:bg-[#284139]/40'}`}
                >
                  <Github className="w-4 h-4" />
                  <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                  }`}>
                    Developer's Github
                  </span>
                </a>
                <a
                  href="https://linkedin.com/in/seandonovan-upenn/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${isDarkMode ? 'bg-[#284139] text-[#809076] hover:text-[#f5f5f5]' : 'bg-[#284139]/20 text-[#E5D9B6] hover:bg-[#284139]/40'}`}
                >
                  <Linkedin className="w-4 h-4" />
                  <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                  }`}>
                    Message Developer
                  </span>
                </a>
                <a
                  href="mailto:donovanafricatech@gmail.com"
                  className={`group relative w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${isDarkMode ? 'bg-[#284139] text-[#809076] hover:text-[#f5f5f5]' : 'bg-[#284139]/20 text-[#E5D9B6] hover:bg-[#284139]/40'}`}
                >
                  <Mail className="w-4 h-4" />
                  <span className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    isDarkMode ? 'bg-[#E5D9B6] text-[#111A19]' : 'bg-[#284139] text-[#E5D9B6]'
                  }`}>
                    Email Developer
                  </span>
                </a>
              </div>
            </div>

            <div className="flex items-center gap-8">
              <a
                href="#"
                className={`text-sm transition-colors duration-200 ${isDarkMode ? 'text-[#809076]/60 hover:text-[#809076]' : 'text-[#E5D9B6]/60 hover:text-[#E5D9B6]'}`}
              >
                Privacy Policy
              </a>
              <a
                href="#"
                className={`text-sm transition-colors duration-200 ${isDarkMode ? 'text-[#809076]/60 hover:text-[#809076]' : 'text-[#E5D9B6]/60 hover:text-[#E5D9B6]'}`}
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
